import React, { useMemo, useState } from 'react';
import Pie from '@components/Widgets/VisualizationWidgets/AvPie';
import Widget from '@components/Widgets/Widget';
import { DateCondition, FeatureFlags, Filter, PeriodBehavior } from '@fabric-ui/components';
import { flex } from '@fabric-ui/theme';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { useWidgetData } from '@utils/dashboardDataUtils';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import stringToFilter from '@utils/stringToFIlter';
import { useSnackbar } from 'notistack';
import { PieWidgetCloneAdapter } from '../../../components/Widgets/VisualizationWidgets/PieWidgetCloneAdapter';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import useQuerySql from '../../../hooks/useQuerySql';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  DwQueryRequest,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { getEntityOverTimeWidget } from '../../Policies/AssetInventoryDashboard/Widgets';
import { filtersFieldToIgnore } from '../ticket.types';
import { useBuildWhereClause } from '../useBuildWhereClause';
import AlertScoreWidgetEmptyState from './AlertScoreWidgetEmptyState';
import { EVariant, IEntityViewChartsProps, ISummarizedCollapseItem } from './types';
import { entityViewWidgetStyle, EntityViewWidgetWrapper, getKeyValueData } from './utils';

const getIncidentsOverTimeQuery = ({
  timeRange,
  dateGap,
  filter,
  secondDimName,
  metricName = 'active_incidents_granular',
}: {
  timeRange: DateCondition;
  dateGap;
  filter?: Filter;
  secondDimName?: string;
  metricName?: string;
}): DwQueryRequest => ({
  groupBy: [dateGap, ...(secondDimName ? [secondDimName] : [])],
  distinct: false,
  filter,
  select: {
    metrics: [
      {
        name: metricName,
        alias: metricName,
      },
    ],
    dims: [
      {
        name: dateGap,
      },
      ...(secondDimName ? [{ name: secondDimName }] : []),
    ],
  },
  sorting: [
    {
      name: dateGap,
      dir: SortDir.ASC,
    },
  ],
  top: {
    size: 100,
    groupOthers: false,
    offset: 0,
  },
  projectionId: {
    name: 'incidents',
    builtIn: true,
  },
  timeRange,
});

const getIncidentByAssigneeWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: {
      title: 'Incident By Assignee',
    },
    coords: {
      x: 0,
      y: 0,
      h: 3,
      w: 0,
    },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: !!filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [
          {
            name: 'incident.assignee_id',
          },
        ],
        metrics: [
          {
            name: 'open_incidents_granular',
            alias: 'open_incidents_granular',
          },
        ],
      },
      groupBy: ['incident.assignee_id'],
      sorting: [
        {
          name: 'open_incidents_granular',
          dir: SortDir.DESC,
        },
      ],
      top: {
        size: 5,
        groupOthers: false,
      },
      projectionId: {
        name: 'incidents',
        builtIn: true,
      },
      distinct: false,
    },
  ],
});

const ScoreBucketsWidget = React.memo(
  ({
    where,
    enabled,
    isScoreEmpty,
    setIsScoreEmpty,
    useMax,
  }: {
    where: string;
    enabled: boolean;
    isScoreEmpty: boolean;
    setIsScoreEmpty: (v: boolean) => void;
    useMax?: boolean;
  }) => {
    const widgetData = useWidgetData();
    const { enqueueSnackbar } = useSnackbar();
    const { featureFlags } = useAvContext();
    const usePieV2 = Boolean(featureFlags[FeatureFlags.PieChartV2]);
    if (isScoreEmpty) {
      return <AlertScoreWidgetEmptyState title={widgetData.incidentScoresBucket.title} variant="pie" />;
    }
    return (
      <Widget
        title={widgetData.incidentScoresBucket.title}
        transformData={data => {
          if (data?.[0]?.res['0 to 0']) {
            setIsScoreEmpty(true);
            return [];
          }
          return widgetData.incidentScoresBucket.transformData?.(data);
        }}
        sql={widgetData.incidentScoresBucket.sql(where, useMax)}
        onError={e => {
          if (e.extensions.message === "Duplicate field key '0 to 0'") {
            setIsScoreEmpty(true);
          } else {
            enqueueSnackbar(e.message, { variant: 'error' });
            console.error(e);
          }
        }}
        metricKey="bucket"
        queryOptions={{ placeholderData: keepPreviousData, enabled }}
        sx={{
          height: 280,
          ...entityViewWidgetStyle,
        }}
        titleVariant="h6">
        {usePieV2 ? (
          <PieWidgetCloneAdapter variant="pie" legendType="table-right" legendProps={{ showValue: false, showShare: true }} />
        ) : (
          <Pie legendProps={{ variant: 'static', maxMetricWidth: 110, showShare: true }} type={PieSubType.Pie} tableSx={{ py: 0 }} />
        )}
      </Widget>
    );
  }
);

const IncidentsViewCharts: React.FC<IEntityViewChartsProps> = ({
  filters: f,
  isExpanded = true,
  setIsExpanded,
  entityTypeId,
  extraFilters,
  searchFields,
}) => {
  const theme = useTheme();
  const widgetData = useWidgetData();
  const {
    accountEntities: { fieldTypeMap },
    featureFlags,
  } = useAvContext();
  const [isScoreEmpty, setIsScoreEmpty] = useState(false);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const filteredFilters = Object.keys(f).reduce(
    (acc, field) =>
      (!filtersFieldToIgnore.includes(field) || field === 'search') && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    { search: [] }
  );
  const entityExtraFiltersObject = stringToFilter(extraFilters || '');
  const regularFilters = filterToExpression(fieldTypeMap, filteredFilters);
  const maxFeatureFlag = featureFlags[FeatureFlags.NormalizedIncidentScore];
  const cleanFilters = cleanEmptyFilters(regularFilters);
  const timeRange = {
    relative: {
      periodBehaviour: PeriodBehavior.last,
      value: 6,
      unit: 'MONTHS',
    },
  };
  const filters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters, entityExtraFiltersObject] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: f?.search?.[0],
    fieldTypeMap,
  });
  const whereSliced = useBuildWhereClause({ filters: f, entityTypeId, searchFields });

  const incidentByAssigneeWidget = getIncidentByAssigneeWidget({ filters, isSmallScreen });

  const { data: topIncidentByAssigneeData, isLoading: topIncidentByAssigneeLoading } = useQueryObject({
    queryObject: incidentByAssigneeWidget.requests.map(request => ({ ...request, top: { ...request.top, size: 2 } })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data =>
      getKeyValueData({
        data,
        dataKey: incidentByAssigneeWidget.requests[0].select.metrics[0].name,
        titleKey: incidentByAssigneeWidget.requests[0].select.dims[0].name,
        nullValueHeader: 'No Assignee',
      }),
  });

  const overTimeReq = getIncidentsOverTimeQuery({
    timeRange,
    dateGap: 'BUCKET_MONTH',
    secondDimName: 'incident.current_status.name',
    filter: cleanFilters,
  });

  const { data: topIncidentStatusOverTimeData, isLoading: topIncidentStatusOverTimeLoading } = useQueryObject({
    queryObject: overTimeReq,
    options: { placeholderData: keepPreviousData, enabled: !isSmallScreen },
    onSuccess: data =>
      getKeyValueData({
        data,
        dataKey: overTimeReq.select.metrics[0].name,
        titleKey: overTimeReq.select.dims[1].name,
        nullValueHeader: 'No Status',
      }),
  });

  const { data: topIncidentScoresBucketData, isLoading: topIncidentScoresBucketLoading } = useQuerySql({
    key: `top${widgetData.incidentScoresBucket.title!}`,
    sql: widgetData.incidentScoresBucket.sql(whereSliced, maxFeatureFlag),
    options: { placeholderData: keepPreviousData, enabled: !isExpanded },
    onSuccess: data =>
      Object.fromEntries(
        Object.entries(data[0].res)
          .sort((a, b) => Number(b[1]) - Number(a[1]))
          .slice(0, 2)
      ),
  });

  const summaryItems: ISummarizedCollapseItem[] = useMemo(
    () => [
      {
        title: 'Incident By Status',
        data: topIncidentStatusOverTimeData,
        isLoading: topIncidentStatusOverTimeLoading,
        showIcon: false,
      },
      ...(!isScoreEmpty
        ? [
            {
              title: 'Risk Mass Spread',
              data: topIncidentScoresBucketData,
              isLoading: topIncidentScoresBucketLoading,
              showIcon: false,
              variant: EVariant.percentage,
            },
          ]
        : []),
      {
        title: 'Incident Assignee',
        data: topIncidentByAssigneeData,
        isLoading: topIncidentByAssigneeLoading,
        showIcon: false,
      },
    ],
    [
      topIncidentScoresBucketData,
      topIncidentScoresBucketLoading,
      topIncidentStatusOverTimeData,
      topIncidentStatusOverTimeLoading,
      topIncidentByAssigneeData,
      topIncidentByAssigneeLoading,
    ]
  );

  return (
    <EntityViewWidgetWrapper
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      summaryItemsProps={{ items: summaryItems, hideDivider: false }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box sx={{ ...flex.row, gap: 1.5, height: '208px' }}>
          <Box sx={{ ...entityViewWidgetStyle, backgroundColor: theme.palette.white.main }}>
            <DraggableWidget
              widget={getEntityOverTimeWidget({
                req: overTimeReq,
              })}
              customToolbar={
                <Box sx={flex.justifyBetweenCenter}>
                  <Typography sx={{ whiteSpace: 'nowrap', mb: 2 }} variant="h6">
                    Number of Incidents by Status
                  </Typography>
                </Box>
              }>
              {renderWidget({
                widget: getEntityOverTimeWidget({
                  req: overTimeReq,
                }),
              })}
            </DraggableWidget>
          </Box>
          <ScoreBucketsWidget
            where={whereSliced}
            enabled={isExpanded}
            isScoreEmpty={isScoreEmpty}
            setIsScoreEmpty={setIsScoreEmpty}
            useMax={maxFeatureFlag}
          />
          <Box
            key={incidentByAssigneeWidget.definition.title.title}
            sx={{
              ...entityViewWidgetStyle,
              position: 'relative',
              backgroundColor: theme => theme.palette.white.main,
            }}>
            <DraggableWidget
              widget={incidentByAssigneeWidget}
              customToolbar={
                <Typography sx={{ whiteSpace: 'nowrap' }} variant="h6">
                  {incidentByAssigneeWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: incidentByAssigneeWidget })}
            </DraggableWidget>
          </Box>
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default IncidentsViewCharts;
